import {changeButtonInner, toggleButton} from '@/components/button/toggleButton';
import type {IDynamicImport} from '@/common/scripts/types/utils';
import type {IYandexMapMarkerData} from '@/components/yandex-map/types';
import {Utils} from '@/common/scripts/utils';
import type ymaps from 'yandex-maps';

export class ProjectMap {
    private readonly wrapperEl: HTMLElement;
    private readonly toggleListWithMapButton: HTMLElement;
    private readonly projectsSection: HTMLElement;
    private readonly mapSection: HTMLElement;
    private readonly map: HTMLElement;
    private ym: ymaps.Map | null = null;

    private readonly activeSectionClass: string = 'is-active';
    private readonly openPopupClass: string = 'is-popup';

    constructor(wrapperEl: HTMLElement) {
        this.wrapperEl = wrapperEl;
        this.toggleListWithMapButton = wrapperEl.querySelector<HTMLElement>('.j-project-map-toggle-btn')!;
        this.projectsSection = wrapperEl.querySelector<HTMLElement>('.j-project-map-list-section')!;
        this.mapSection = wrapperEl.querySelector<HTMLElement>('.j-project-map-map-section')!;
        this.map = this.mapSection.querySelector<HTMLElement>('.j-project-map-map')!;

        this.init();
    }

    private init(): void {
        this.initYandexMap();
        this.setupEventListeners();
    }

    private setupEventListeners(): void {
        toggleButton(this.toggleListWithMapButton, 'Проекты списком', () => {
            return this.toggleListWithMap();
        });

        const mobileButtonCloseMap = this.map.querySelector<HTMLElement>('.j-map-close')!;

        mobileButtonCloseMap.addEventListener('click', () => {
            this.toggleListWithMap();
            changeButtonInner(this.toggleListWithMapButton, 'Проекты на карте');
        });

        window.addEventListener('clickYMMarker', ((event: CustomEvent) => {
            this.openProjectInfo(event.detail);
        }) as EventListener);
    }

    private toggleListWithMap(): void {
        const isMobile = Utils.isMobile();

        if (isMobile) {
            this.handleMobileToggle();
        } else {
            this.handleDesktopToggle();
        }
    }

    private handleMobileToggle(): void {
        const isPopupOpen = this.mapSection.classList.contains(this.openPopupClass);

        if (!isPopupOpen) {
            this.projectsSection.classList.remove(this.activeSectionClass);
            this.mapSection.classList.add(this.activeSectionClass, this.openPopupClass);
            Utils.bodyFixed();
        } else if (isPopupOpen) {
            this.mapSection.classList.remove(this.activeSectionClass, this.openPopupClass);
            this.projectsSection.classList.add(this.activeSectionClass);
            Utils.bodyStatic();
        }
    }

    private handleDesktopToggle(): void {
        const isListActive = this.projectsSection.classList.contains(this.activeSectionClass);

        if (isListActive) {
            this.projectsSection.classList.remove(this.activeSectionClass);
            this.mapSection.classList.add(this.activeSectionClass);
        } else {
            this.projectsSection.classList.add(this.activeSectionClass);
            this.mapSection.classList.remove(this.activeSectionClass);
        }
    }

    private initYandexMap(): void {
        Utils.checkMap(() => {
            const ymaps = window.ymaps;

            ymaps.ready(() => {
                import(/* webpackChunkName: "yandex-map" */ '@/components/yandex-map')
                    .then(async({default: YandexMap}: IDynamicImport) => {
                        this.ym = await new YandexMap(ymaps).init({
                            wrapper                : this.map,
                            cluster                : true,
                            fullScreenControl      : false,
                            customFullScreenControl: false,
                            customZoomControl      : true
                        });
                    });
            });
        });
    }

    private openProjectInfo(placemark: ymaps.Placemark): void {
        this.wrapperEl.classList.add('is-info-open');

        const nativeData = placemark.options.getNative('data') as unknown as IYandexMapMarkerData;
        const projectInfoInner = this.wrapperEl.querySelector<HTMLElement>('.j-projects-map-info-inner')!;
        const closeButton = this.wrapperEl.querySelector('.j-projects-map-info-close-btn')!;
        const projectElement = this.projectsSection.querySelector(`[data-id="${nativeData.id}"]`);

        if (projectElement) {
            projectInfoInner.innerHTML = '';
            const projectElementClone = projectElement.cloneNode(true) as HTMLElement;

            projectElementClone.classList.add('project_theme_map');
            projectInfoInner.appendChild(projectElementClone);

            const projectElementCloneHeight = projectElementClone!.getBoundingClientRect().height;
            const newMapHeight = window.innerHeight - projectElementCloneHeight;

            this.map.style.minHeight = `${newMapHeight}px`;

            if (!this.ym) {
                return;
            }

            this.ym.container.fitToViewport();
            const coords = placemark.geometry!.getCoordinates();

            this.ym.setCenter(coords!, this.ym.getZoom(), {
                duration: 300
            });

            const closeHandler = (): void => {
                this.wrapperEl.classList.remove('is-info-open');
                const prevActiveMarker = this.map.querySelector('.j-yandex-map-marker.is-active');

                if (prevActiveMarker) {
                    prevActiveMarker.classList.remove('is-active');
                }
                if (this.ym) {
                    this.ym.balloon.close();
                    this.ym.container.fitToViewport();
                }
                closeButton.removeEventListener('click', closeHandler);
            };

            closeButton.addEventListener('click', closeHandler);
        }
    }
}
